import { ApiError, describeStatus } from '@/api/envelope';

/** The three requests of signing in, each with its own sentence for a refusal. */
export type SignInStep = 'password' | 'code' | 'resend';

export const NOT_RECOGNISED = 'That email address or password was not recognised.';
export const TOO_MANY_ATTEMPTS = 'Too many attempts. Wait a few minutes and try again.';
const CODE_NOT_CORRECT = 'That code was not correct. Please try again.';
const CODE_NOT_SENT = 'We could not send another code. Please try again.';
const DID_NOT_COMPLETE =
  'Signing in did not complete. Please enter your email and password again.';

/** What each step says when it is refused and the server gave no wording of its own. */
const REFUSED: Record<SignInStep, string> = {
  password: NOT_RECOGNISED,
  code: CODE_NOT_CORRECT,
  resend: CODE_NOT_SENT,
};

/**
 * What a failed sign-in request says, by what actually happened
 * (arc-and-discovery-hardening, D16, B26, B27).
 *
 * A rate limit, an unreachable server and a server fault each get their own
 * sentence, at every step: told "not recognised", people retyped a correct
 * password while the real cause stayed hidden. A refusal keeps each step's
 * own wording - the password step never says which of the two was wrong,
 * which would tell an attacker which addresses are registered, and a refused
 * code keeps the server's wording, which says why.
 */
export function signInFailure(error: unknown, step: SignInStep): string {
  // `fetch` itself throws a TypeError when no reply arrives at all.
  if (error instanceof TypeError) return describeStatus(0);
  if (!(error instanceof ApiError)) return REFUSED[step];

  if (error.status === 429) return TOO_MANY_ATTEMPTS;
  if (error.status === 0) return describeStatus(0);
  // Whatever the body: a proxy's error page is HTML, not words for a person.
  if (error.status >= 500) return describeStatus(error.status);
  if (step === 'password') return REFUSED[step];
  // After the password step a 422 means the request itself was malformed - the
  // login token missing, say - and the backend's words for that name a field
  // ("Login_Token is required"), which means nothing to the person.
  if (error.status === 422) return DID_NOT_COMPLETE;
  if (step === 'resend') return REFUSED[step];
  return error.serverMessage ?? REFUSED[step];
}
